/**
 * AmbienceEditor.jsx — the sound under the words.
 *
 * Two sources: Rammy's own generated loops (always available, listenable
 * before you commit) and a described one handed to ElevenLabs sound effects.
 */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import api from "@/lib/api";
import * as L from "lucide-react";

const DEFAULT_VOLUME = 0.18;

export function AmbienceEditor({ ambience, onChange }) {
  const enabled = !!ambience;
  const volume = ambience?.volume ?? DEFAULT_VOLUME;
  const source = ambience?.source || "library";
  const [library, setLibrary] = useState([]);
  const [auditioning, setAuditioning] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/studio/ambience/library")
      .then(r => setLibrary(r.data?.ambiences || []))
      .catch(() => {});
    return () => { try { audioRef.current?.pause(); } catch (_) { /* ignore */ } };
  }, []);

  const toggle = (on) => {
    if (!on) { onChange(null); return; }
    onChange({ source: "library", slug: library[0]?.slug || "rain", prompt: null, volume });
  };

  const audition = async (slug) => {
    try { audioRef.current?.pause(); } catch (_) { /* ignore */ }
    if (auditioning === slug) { setAuditioning(null); return; }
    setAuditioning(slug);
    try {
      const res = await api.get(`/studio/ambience/${slug}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = new Audio(url);
      el.loop = true;
      el.volume = 0.6;
      audioRef.current = el;
      el.addEventListener("pause", () => setTimeout(() => URL.revokeObjectURL(url), 2000));
      await el.play();
    } catch (_) {
      setAuditioning(null);
      toast.error("Couldn't play that ambience");
    }
  };

  return (
    <div className="rounded-2xl bg-[var(--rammy-cyan)]/5 border border-[var(--rammy-cyan)]/30 p-4" data-testid="studio-ambience">
      <div className="flex items-center justify-between mb-2 gap-3">
        <div className="flex items-center gap-2">
          <L.Waves size={14} className="text-[var(--rammy-cyan-dark)]"/>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">Ambience</div>
          <span className="text-[11px] text-[var(--rammy-text-muted)]">Optional — sits under the narration.</span>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="studio-ambience-toggle">
          <span className="text-[11px] text-[var(--rammy-text-sec)]">{enabled ? "On" : "Off"}</span>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={() => toggle(!enabled)}
            className={`w-9 h-5 rounded-full transition-colors relative ${enabled ? "bg-[var(--rammy-cyan-dark)]" : "bg-[var(--rammy-border)]"}`}
          >
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-[var(--rm-surface)] shadow transition-all ${enabled ? "left-[18px]" : "left-0.5"}`}/>
          </button>
        </label>
      </div>

      {enabled && (
        <div className="space-y-3 mt-3">
          {/* Source switch */}
          <div className="inline-flex rounded-full bg-[var(--rammy-surface-alt)] p-1" data-testid="studio-ambience-source">
            {[
              { id: "library", label: "Rammy's loops" },
              { id: "custom", label: "Describe your own" },
            ].map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange(opt.id === "library"
                  ? { source: "library", slug: ambience?.slug || library[0]?.slug || "rain", prompt: null, volume }
                  : { source: "custom", slug: null, prompt: ambience?.prompt || "gentle rain on leaves at dusk", volume })}
                data-active={source === opt.id ? "true" : "false"}
                data-testid={`studio-ambience-source-${opt.id}`}
                className={`px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
                  source === opt.id ? "bg-[var(--rm-surface)] text-[var(--rammy-text)] shadow-sm" : "text-[var(--rammy-text-sec)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {source === "library" ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="studio-ambience-library">
              {library.map(item => {
                const active = ambience?.slug === item.slug;
                return (
                  <div
                    key={item.slug}
                    className={`rounded-xl border p-2.5 transition-colors ${
                      active
                        ? "border-[var(--rammy-cyan-dark)] bg-[var(--rammy-cyan)]/12"
                        : "border-[var(--rammy-border)] bg-[var(--rm-surface)] hover:border-[var(--rammy-cyan)]"
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => onChange({ source: "library", slug: item.slug, prompt: null, volume })}
                        className="text-left flex-1 min-w-0"
                        data-testid={`studio-ambience-pick-${item.slug}`}
                      >
                        <div className="text-[12.5px] font-medium truncate">{item.label}</div>
                        <div className="text-[10.5px] text-[var(--rammy-text-muted)] truncate">{item.description}</div>
                      </button>
                      <button
                        type="button"
                        onClick={() => audition(item.slug)}
                        className="text-[var(--rammy-cyan-dark)] hover:text-[var(--rammy-text)] shrink-0 transition-colors"
                        data-testid={`studio-ambience-audition-${item.slug}`}
                        aria-label={`Hear ${item.label}`}
                      >
                        {auditioning === item.slug ? <L.Pause size={13}/> : <L.Play size={13}/>}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <Input
                value={ambience?.prompt || ""}
                onChange={(e) => onChange({ source: "custom", slug: null, prompt: e.target.value, volume })}
                placeholder="Describe your ambience — e.g. 'thunder rolling over distant hills'"
                className="rounded-xl h-9 text-sm border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
                maxLength={200}
                data-testid="studio-ambience-prompt"
              />
              <div className="text-[10.5px] text-[var(--rammy-text-muted)] leading-relaxed flex items-start gap-1.5">
                <L.Info size={12} className="mt-0.5 shrink-0"/>
                Described ambience is generated by a third-party sound model. If it's unavailable your
                render still completes — narration only. Rammy's own loops always work.
              </div>
            </>
          )}

          <div className="flex items-center gap-3">
            <span className="text-[11px] text-[var(--rammy-text-muted)] w-16 shrink-0">Level</span>
            <Slider
              min={0}
              max={60}
              step={1}
              value={[Math.round(volume * 100)]}
              onValueChange={([v]) => onChange({ ...ambience, volume: v / 100 })}
              className="flex-1"
              data-testid="studio-ambience-volume"
            />
            <span className="text-[11px] text-[var(--rammy-text-sec)] w-10 text-right">{Math.round(volume * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
}
